import { Security, SecurityCategory_CategoryCode } from '../proto/generated/security'

export enum QuickTradeQuantityType {
    LOTS = 'Lots',
    QUANTITY = 'Quantity',
    AMOUNT = 'Amount',
}

export function effectiveLotSize(lotSize?: number | null): number {
    return lotSize && lotSize > 0 ? lotSize : 1
}

export function isFoSecurity(sec?: Pick<Security, 'categoryCode' | 'lotSize'> | null): boolean {
    if (!sec) return false
    if (
        [SecurityCategory_CategoryCode.OPTIONS, SecurityCategory_CategoryCode.FUTURES].includes(
            sec.categoryCode
        )
    ) {
        return true
    }
    return effectiveLotSize(sec.lotSize) > 1
}

export function parseTemplateQuantityInput(
    raw: string,
    quantityType: QuickTradeQuantityType | 'Multiplier'
): number {
    if (raw === '' || raw === '-') return 0
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 0) return 0
    if (quantityType === QuickTradeQuantityType.LOTS || quantityType === QuickTradeQuantityType.AMOUNT) {
        return Math.floor(n)
    }
    return n
}

/** Exchange qty snapped down to whole lots (F&O place path). */
export function roundQtyDownToLotMultiple(qty: number, lotSize?: number | null): number {
    if (qty <= 0) return 0
    const ls = effectiveLotSize(lotSize)
    if (ls <= 1) return Math.floor(qty)
    return Math.floor(qty / ls) * ls
}

/** Nearest lot multiple for qty input blur (matches Quick Trade window). */
export function roundQtyToNearestLotMultiple(qty: number, lotSize?: number | null): number {
    if (qty <= 0) return 0
    const ls = effectiveLotSize(lotSize)
    if (ls <= 1) return Math.floor(qty)
    return Math.round(qty / ls) * ls
}

/** Decimal RHS preview: units (stocks / F&O lots) or lots (F&O amount). */
export function computeDisplaySecondaryFromPrimary(
    type: QuickTradeQuantityType,
    primary: number,
    lotSize: number,
    unitPriceDisplay: number | undefined,
    foQuantityMode: boolean
): number | undefined {
    if (!Number.isFinite(primary) || primary < 0) return undefined
    if (primary === 0) return 0
    const ls = effectiveLotSize(lotSize)
    switch (type) {
        case QuickTradeQuantityType.LOTS:
            return primary * ls
        case QuickTradeQuantityType.QUANTITY:
            return primary
        case QuickTradeQuantityType.AMOUNT:
            if (!unitPriceDisplay || unitPriceDisplay <= 0) return undefined
            if (foQuantityMode && ls > 1) return primary / unitPriceDisplay / ls
            return primary / unitPriceDisplay
        default:
            return undefined
    }
}

/** Integer exchange qty sent on place order (lot-rounded when lotSize > 1). */
export function computeOrderQtyFromPrimary(
    type: QuickTradeQuantityType,
    primary: number,
    lotSize: number,
    unitPriceDisplay: number | undefined
): number {
    const ls = effectiveLotSize(lotSize)
    if (!Number.isFinite(primary) || primary < 0) return 0
    if (primary === 0) return 0
    switch (type) {
        case QuickTradeQuantityType.LOTS:
            return primary * ls
        case QuickTradeQuantityType.QUANTITY:
            return roundQtyDownToLotMultiple(primary, ls)
        case QuickTradeQuantityType.AMOUNT:
            if (!unitPriceDisplay || unitPriceDisplay <= 0) return 0
            return roundQtyDownToLotMultiple(Math.floor(primary / unitPriceDisplay), ls)
        default:
            return 0
    }
}

export function computeOrderQtyFromTemplateParams(
    params: { lots?: number | null; quantity?: number | null; amount?: number | null },
    lotSize: number,
    unitPriceDisplay?: number
): number {
    if (params.lots != null) {
        return computeOrderQtyFromPrimary(QuickTradeQuantityType.LOTS, params.lots, lotSize, unitPriceDisplay)
    }
    if (params.quantity != null) {
        return computeOrderQtyFromPrimary(QuickTradeQuantityType.QUANTITY, params.quantity, lotSize, unitPriceDisplay)
    }
    if (params.amount != null) {
        return computeOrderQtyFromPrimary(QuickTradeQuantityType.AMOUNT, params.amount, lotSize, unitPriceDisplay)
    }
    return 0
}

export function inputStepForQuantityType(type: QuickTradeQuantityType, lotSize: number): number {
    const ls = effectiveLotSize(lotSize)
    if (type === QuickTradeQuantityType.QUANTITY && ls > 1) return ls
    return 1
}

export function roundPrimaryInputOnBlur(
    type: QuickTradeQuantityType,
    primary: number,
    lotSize: number
): number {
    if (!Number.isFinite(primary) || primary < 0) return 0
    if (type === QuickTradeQuantityType.QUANTITY) {
        return roundQtyToNearestLotMultiple(primary, lotSize)
    }
    return Math.floor(primary)
}

export type ValidateTemplateQuantityInput = {
    quantityType: QuickTradeQuantityType
    lots?: number | null
    quantity?: number | null
    amount?: number | null
    multiplier?: number | null
    lotSize: number
    foQuantityMode: boolean
    unitPriceDisplay?: number
}

export function validateTemplateQuantityForSave(
    input: ValidateTemplateQuantityInput
): { ok: true } | { ok: false; message: string } {
    const { quantityType, lotSize, foQuantityMode, unitPriceDisplay } = input
    const ls = effectiveLotSize(lotSize)
    const minExchangeQty = foQuantityMode ? ls : 1

    if (input.multiplier != null) {
        return { ok: false, message: 'Multiplier is not supported for buy/sell templates' }
    }

    switch (quantityType) {
        case QuickTradeQuantityType.LOTS: {
            const lots = input.lots ?? 0
            if (lots < 1) {
                return { ok: false, message: 'Minimum 1 lot required' }
            }
            const exchangeQty = computeOrderQtyFromPrimary(
                QuickTradeQuantityType.LOTS,
                lots,
                lotSize,
                unitPriceDisplay
            )
            if (exchangeQty < minExchangeQty) {
                return { ok: false, message: 'Minimum 1 lot required' }
            }
            break
        }
        case QuickTradeQuantityType.QUANTITY: {
            const qty = input.quantity ?? 0
            if (qty < 1) {
                return { ok: false, message: foQuantityMode ? `Minimum ${ls} units (1 lot) required` : 'Minimum 1 unit required' }
            }
            const exchangeQty = computeOrderQtyFromPrimary(
                QuickTradeQuantityType.QUANTITY,
                qty,
                lotSize,
                unitPriceDisplay
            )
            if (exchangeQty < minExchangeQty) {
                return {
                    ok: false,
                    message: foQuantityMode ? `Minimum ${ls} units (1 lot) required` : 'Minimum 1 unit required',
                }
            }
            break
        }
        case QuickTradeQuantityType.AMOUNT: {
            const amount = input.amount ?? 0
            if (amount <= 0) {
                return { ok: false, message: 'Amount must be greater than 0' }
            }
            if (!unitPriceDisplay || unitPriceDisplay <= 0) {
                return { ok: false, message: 'Cannot validate amount without a live price' }
            }
            const exchangeQty = computeOrderQtyFromPrimary(
                QuickTradeQuantityType.AMOUNT,
                amount,
                lotSize,
                unitPriceDisplay
            )
            if (exchangeQty < minExchangeQty) {
                const label = foQuantityMode ? `1 lot (${ls} units)` : '1 unit'
                return { ok: false, message: `Minimum ${label}  is required` }
            }
            break
        }
    }
    return { ok: true }
}
